import React, {useMemo, useState} from 'react';

import {TimestampAwareMarkdown} from '@/components/timestamp_aware_markdown';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
dayjs.extend(utc);

import {useSelectorWithRefresh} from '@/store/kysely_module';
import {NestedComment} from '@/store/selectors/selectors';

import {CommentForm} from './comment_form';
import {ActionIcon, Avatar, Group, Menu, Paper, Text, TypographyStylesProvider} from '@mantine/core';
import {useModals} from '../modals/modals_provider';
import {useIsMobile} from '@/utils/user_agent';
import {IconArrowForward, IconDotsVertical} from '@tabler/icons-react';

import './comments.css';
import {useCurrentUser} from '@/modules/music_sniper/hooks/useCurrentUser';

type CommentProps = {
    comment: NestedComment;
    clickedTimestamp: (timestamp: string) => void;
    deleteComment: (commentId: string) => void;
    editComment: (commentId: string, text: string) => void;
    replyToComment: (text: string, parentId: string) => Promise<void>;
    isReply?: boolean;
    isLastReply?: boolean;
    showUser?: boolean;
    hideAvatar?: boolean;
    onInitialFocus?: () => string;
    variant?: 'full' | 'compact';
    hideActions?: boolean;
};

export const Comment = (props: CommentProps) => {
    const currentUser = useCurrentUser();
    const isAuthorCurrentUser = props.comment.created_by === currentUser?.id;
    const [isEditing, setIsEditing] = useState(false);
    const modals = useModals();

    const [author] = useSelectorWithRefresh('getUser', {user_id: props.comment.created_by}, null, {workspace_id: props.comment.workspace_id});

    const [isReplying, setIsReplying] = useState(false);

    const handleDelete = async () => {
        modals.openModal('ConfirmationModal', {
            title: 'Delete this comment?',
            message: 'This action cannot be undone.',
            confirmLabel: 'Delete',
            danger: true,
            onConfirm: () => {
                props.deleteComment(props.comment.id);
            },
        });
    };

    const handleReplyClick = () => {
        setIsReplying(!isReplying);
    };

    const timeDisplay = useMemo(() =>
        dayjs.utc(props.comment.created_at, 'YYYY-MM-DD HH:mm:ss').local().fromNow(),
        [props.comment.created_at]);

    const isMobile = useIsMobile();

    const actionMenu = !props.hideActions && (
        <Menu
            closeOnItemClick={true}
        >
            <Menu.Target>
                <ActionIcon
                    variant='hover'
                    style={{
                        position: 'absolute',
                        right: 0,
                        backgroundColor: 'transparent',
                        color: 'var(--mantine-color-text)',
                    }}
                >
                    <IconDotsVertical
                        size='16px'
                        strokeWidth={1}
                    />
                </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
                <Menu.Item
                    onClick={() => {
                        setIsEditing(true);
                    }}
                >
                    Edit
                </Menu.Item>
                <Menu.Item
                    onClick={handleDelete}
                >
                    Delete
                </Menu.Item>
            </Menu.Dropdown>
        </Menu>
    );

    const replyIcon = props.variant !== 'compact' && (
        <ActionIcon
            variant='hover'
            style={{
                backgroundColor: 'transparent',
            }}
            onClick={handleReplyClick}
        >
            <IconArrowForward
                style={{color: 'var(--mantine-color-text)'}}
                strokeWidth={0.8}
                size={'1.2rem'}
            />
        </ActionIcon>
    );

    const showReplyArrow = (
        (!props.isReply && !Boolean(props.comment.replies.length)) ||
        (props.isReply && props.isLastReply)
    );

    const commentOrEditor = isEditing ? (
        <CommentForm
            initialValue={props.comment.text}
            isReply={props.isReply}
            onSubmit={async text => {
                props.editComment(props.comment.id, text);
                setIsEditing(false);
            }}
        />
    ) : (
        <CommentMarkdown
            clickedTimestamp={props.clickedTimestamp}
            text={props.comment.text}
        />
    );

    return (
        <Paper
            // withBorder
            radius='md'
            style={{
                paddingLeft: (!isMobile || props.isReply) ? 'var(--mantine-spacing-lg)' : '',
                // padding: !isMobile ? 'var(--mantine-spacing-lg) var(--mantine-spacing-xl)' : '',
                maxWidth: '600px',
                marginBottom: props.variant === 'compact' ? '0' : '10px',
                position: 'relative',
            }}
        >
            <Group>
                {isAuthorCurrentUser && actionMenu}
                {props.showUser && !props.hideAvatar && author && (
                    <Avatar
                        src={'https://cdn3.iconfinder.com/data/icons/musician-1/230/musician-002-512.png'} // TODO: use profile picture
                        alt=''
                        radius={'xl'}
                        size={props.variant === 'compact' ? 'sm' : 'md'}
                        classNames={{image: 'avatar-image'}}
                    />
                )}
                <div>
                    {props.showUser && (
                        <Text size={props.variant === 'compact' ? 'xs' : 'sm'}>
                            {author?.name || 'user'}
                        </Text>
                    )}
                    {props.variant !== 'compact' && (
                        <Text size="xs" c="dimmed">
                            {timeDisplay}
                            {/* {actions} */}
                        </Text>
                    )}
                </div>
            </Group>
            <TypographyStylesProvider
                // className={classes.body}
                style={{
                    paddingLeft: '54px',
                    paddingTop: 'var(--mantine-spacing-sm)',
                    paddingBottom: 'var(--mantine-spacing-lg)',
                    fontSize: 'var(--mantine-font-size-sm)',
                    userSelect: 'text',
                }}
            >
                {props.variant === 'compact' && (
                    <Text size="xs" c="dimmed" style={{fontSize: '10px'}}>
                        {timeDisplay}
                    </Text>
                )}
                {commentOrEditor}
                {showReplyArrow && replyIcon}
            </TypographyStylesProvider>
            {/* <div>
                {actions}
            </div> */}
            {/* </div> */}
            {
                Boolean(props.comment.replies.length) && (
                    <div>
                        {props.comment.replies.map((c, i) => (
                            <Comment
                                {...props}
                                key={c.id}
                                replyToComment={text => props.replyToComment(text, props.comment.id)}
                                // showUser={(i === 0) || (props.comment.replies[i].created_by !== c.created_by)}
                                showUser={true}
                                comment={c}
                                isReply={true}
                                isLastReply={i === props.comment.replies.length - 1}
                            />
                        ))}
                    </div>
                )
            }
            {isReplying && (
                <CommentForm
                    onInitialFocus={props.onInitialFocus}
                    isReply={true}
                    onCancel={() => setIsReplying(false)}
                    onSubmit={async (text) => {
                        await props.replyToComment(text, props.comment.id);
                        setIsReplying(false);
                    }} />
            )}
        </Paper>
    );
};

type CommentMarkdownProps = {
    text: string;
    clickedTimestamp: (timestamp: string) => void;
};

export const CommentMarkdown = (props: CommentMarkdownProps) => {
    return (
        <TimestampAwareMarkdown
            text={props.text}
            clickedTimestamp={props.clickedTimestamp}
            className='comment-body'
        />
    );
}

